(() => {
  const ROOT_SELECTOR = '[data-hgb-archive-collection]';
  const SIZE_PARAM = 'filter.v.option.talla';

  const normalizeSize = (value) => (value || '').trim().toUpperCase();

  const buildSizeUrl = (size) => {
    const url = new URL(window.location.href);
    const normalizedSize = normalizeSize(size);

    url.searchParams.delete('talla');
    url.searchParams.delete('size');
    url.searchParams.delete('page');

    if (normalizedSize) {
      url.searchParams.set(SIZE_PARAM, normalizedSize);
    } else {
      url.searchParams.delete(SIZE_PARAM);
    }

    return `${url.pathname}${url.search}${url.hash}`;
  };

  const readSelectedSize = () => {
    const params = new URLSearchParams(window.location.search);
    return normalizeSize(
      params.get(SIZE_PARAM) || params.get('talla') || params.get('size')
    );
  };

  const readChipSize = (chip) => {
    if (chip.dataset.hgbArchiveSize !== undefined) {
      return normalizeSize(chip.dataset.hgbArchiveSize);
    }

    const href = chip.getAttribute('href');
    if (!href) return '';

    try {
      const url = new URL(href, window.location.origin);
      return normalizeSize(
        url.searchParams.get(SIZE_PARAM) ||
        url.searchParams.get('talla') ||
        url.searchParams.get('size')
      );
    } catch (error) {
      return '';
    }
  };

  const collectProductMeta = (root) => {
    const productMeta = new Map();

    root.querySelectorAll('[data-hgb-archive-product-meta]').forEach((meta) => {
      const productId = String(meta.dataset.productId || '');
      if (!productId) return;

      const sizes = (meta.dataset.sizes || '')
        .split('||')
        .map(normalizeSize)
        .filter(Boolean);

      const inventory = Number.parseInt(meta.dataset.inventory || '', 10);

      productMeta.set(productId, {
        sizes: Array.from(new Set(sizes)),
        inventory: Number.isFinite(inventory) ? inventory : null
      });
    });

    return productMeta;
  };

  const enrichProductCards = (root, productMeta) => {
    productMeta.forEach((data, productId) => {
      const card = root.querySelector(
        `.grid-product[data-product-id="${productId}"]`
      );

      if (!card) return;

      const target = card.querySelector('.grid-product__meta');
      if (!target || target.querySelector('.hgb-archive-card__availability')) return;
      if (!data.sizes.length) return;

      const wrapper = document.createElement('div');
      wrapper.className = 'hgb-archive-card__availability';

      const sizesLine = document.createElement('span');
      sizesLine.className = 'hgb-archive-card__sizes';
      sizesLine.textContent =
        data.sizes.length === 1
          ? `Última talla: ${data.sizes[0]}`
          : `Disponible en: ${data.sizes.join(' · ')}`;

      wrapper.appendChild(sizesLine);

      if (data.inventory !== null && data.inventory > 0 && data.inventory <= 3) {
        const lowStockLine = document.createElement('span');
        lowStockLine.className = 'hgb-archive-card__low-stock';
        lowStockLine.textContent =
          data.inventory === 1 ? 'Última unidad' : `Solo quedan ${data.inventory}`;
        wrapper.appendChild(lowStockLine);
      }

      target.appendChild(wrapper);
    });
  };

  const updateChipCounts = (root, productMeta) => {
    const counts = new Map();

    productMeta.forEach(({ sizes }) => {
      sizes.forEach((size) => {
        counts.set(size, (counts.get(size) || 0) + 1);
      });
    });

    root.querySelectorAll('.hgb-archive__size-chip').forEach((chip) => {
      const size = readChipSize(chip);
      chip.dataset.hgbArchiveSize = size;

      if (!size) return;

      let count = chip.querySelector('small');
      if (!count) {
        count = document.createElement('small');
        chip.appendChild(count);
      }

      count.textContent = String(counts.get(size) || 0);
    });
  };

  const ensureEmptyState = (root) => {
    let emptyState = root.querySelector('[data-hgb-archive-empty]');
    if (emptyState) return emptyState;

    const grid = root.querySelector('.product-grid-container');
    if (!grid) return null;

    emptyState = document.createElement('div');
    emptyState.className = 'hgb-archive__empty';
    emptyState.dataset.hgbArchiveEmpty = '';
    emptyState.setAttribute('role', 'status');
    emptyState.setAttribute('aria-live', 'polite');
    emptyState.hidden = true;
    emptyState.style.cssText =
      'padding:40px 20px;text-align:center;border-top:1px solid rgba(17,17,17,.16);border-bottom:1px solid rgba(17,17,17,.16);';

    const title = document.createElement('strong');
    title.textContent = 'No encontramos prendas disponibles en esta talla.';

    const message = document.createElement('p');
    message.textContent = 'Prueba otra talla para ver el inventario actual.';
    message.style.margin = '8px 0 0';

    emptyState.append(title, message);
    grid.insertAdjacentElement('afterend', emptyState);

    return emptyState;
  };

  const ensureClearLink = (root) => {
    const resultsBar = root.querySelector('.hgb-archive__results-bar');
    if (!resultsBar) return null;

    let clearLink =
      resultsBar.querySelector('[data-hgb-archive-clear]') ||
      resultsBar.querySelector('a');

    if (!clearLink) {
      clearLink = document.createElement('a');
      clearLink.textContent = 'Limpiar talla';
      resultsBar.appendChild(clearLink);
    }

    clearLink.dataset.hgbArchiveClear = '';
    clearLink.href = buildSizeUrl('');

    if (clearLink.dataset.hgbArchiveBound !== 'true') {
      clearLink.dataset.hgbArchiveBound = 'true';
      clearLink.addEventListener('click', (event) => {
        event.preventDefault();
        applySize(root, '', true);
      });
    }

    return clearLink;
  };

  const updateResultCount = (root, visibleCount) => {
    const paragraph = root.querySelector('.hgb-archive__results-bar p');
    if (!paragraph) return;

    paragraph.setAttribute('aria-live', 'polite');
    paragraph.replaceChildren();

    const strong = document.createElement('strong');
    strong.textContent = String(visibleCount);

    paragraph.append(
      strong,
      document.createTextNode(
        visibleCount === 1 ? ' producto disponible' : ' productos disponibles'
      )
    );
  };

  const applySize = (root, requestedSize, updateHistory = false) => {
    const selectedSize = normalizeSize(requestedSize);
    const productMeta = collectProductMeta(root);
    const cards = root.querySelectorAll('.grid-product[data-product-id]');
    let visibleCount = 0;

    cards.forEach((card) => {
      const data = productMeta.get(String(card.dataset.productId || ''));
      const isVisible =
        !selectedSize || Boolean(data && data.sizes.includes(selectedSize));

      card.hidden = !isVisible;
      if (isVisible) visibleCount += 1;
    });

    root.querySelectorAll('.hgb-archive__size-chip').forEach((chip) => {
      const chipSize = readChipSize(chip);
      const isActive = chipSize === selectedSize;

      chip.dataset.hgbArchiveSize = chipSize;
      chip.classList.toggle('is-active', isActive);

      if (isActive) {
        chip.setAttribute('aria-current', 'page');
      } else {
        chip.removeAttribute('aria-current');
      }
    });

    root.dataset.hgbArchiveSelectedSize = selectedSize;
    updateResultCount(root, visibleCount);

    const clearLink = ensureClearLink(root);
    if (clearLink) {
      clearLink.hidden = !selectedSize;
      clearLink.href = buildSizeUrl('');
    }

    const emptyState = ensureEmptyState(root);
    if (emptyState) {
      emptyState.hidden = visibleCount !== 0;
    }

    if (updateHistory) {
      window.history.pushState(
        { hgbArchiveSize: selectedSize },
        '',
        buildSizeUrl(selectedSize)
      );
    }
  };

  const bindSizeChips = (root) => {
    root.querySelectorAll('.hgb-archive__size-chip').forEach((chip) => {
      chip.dataset.hgbArchiveSize = readChipSize(chip);

      if (chip.dataset.hgbArchiveBound === 'true') return;
      chip.dataset.hgbArchiveBound = 'true';

      chip.addEventListener('click', (event) => {
        event.preventDefault();

        const chipSize = normalizeSize(chip.dataset.hgbArchiveSize);
        const currentSize = normalizeSize(root.dataset.hgbArchiveSelectedSize);
        const nextSize = chipSize && chipSize === currentSize ? '' : chipSize;

        applySize(root, nextSize, true);
      });
    });
  };

  const syncArchive = (root) => {
    const productMeta = collectProductMeta(root);

    enrichProductCards(root, productMeta);
    updateChipCounts(root, productMeta);
    bindSizeChips(root);
    applySize(root, readSelectedSize(), false);
  };

  const setupArchive = (root) => {
    if (root.__hgbArchiveObserver) {
      syncArchive(root);
      return;
    }

    syncArchive(root);

    let syncQueued = false;
    const observerOptions = { childList: true, subtree: true };
    const observer = new MutationObserver(() => {
      if (syncQueued) return;
      syncQueued = true;

      window.requestAnimationFrame(() => {
        syncQueued = false;
        observer.disconnect();
        syncArchive(root);
        observer.observe(root, observerOptions);
      });
    });

    observer.observe(root, observerOptions);
    root.__hgbArchiveObserver = observer;
  };

  const init = () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach(setupArchive);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  window.addEventListener('popstate', () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      applySize(root, readSelectedSize(), false);
    });
  });

  document.addEventListener('shopify:section:load', init);
})();
